import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export function useRecordSheetSelection(paramName: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedId = searchParams.get(paramName);

  const openRecord = useCallback((id: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (paramName === "assignment") {
        next.delete("submission");
        next.delete("owner");
      }
      next.set(paramName, id);
      return next;
    });
  }, [paramName, setSearchParams]);

  const closeRecord = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(paramName);
    if (paramName === "assignment") {
      next.delete("submission");
      next.delete("owner");
    }
    const state = location.state as { assignmentReturnHref?: unknown } | null;
    const savedReturn = paramName === "assignment" && typeof state?.assignmentReturnHref === "string"
      ? new URL(state.assignmentReturnHref, "https://course-return.invalid") : null;
    const exactReturn = savedReturn?.origin === "https://course-return.invalid"
      && savedReturn.pathname === location.pathname && !savedReturn.searchParams.has("assignment") ? savedReturn : null;
    navigate({ pathname: location.pathname, search: exactReturn?.search ?? next.toString(), hash: exactReturn?.hash ?? location.hash }, {
      replace: true,
      state: location.state,
    });
  }, [paramName, searchParams, navigate, location]);

  // Course sheets consume the complete server destination, including its exact
  // paired identity. Queue movement replaces the current sheet history entry.
  const openDestination = useCallback((destination: string, replace = false) => {
    navigate(destination, { replace, state: location.state });
  }, [navigate, location.state]);

  return { selectedId, openRecord, closeRecord, openDestination };
}
